/**
 * Custom Hook for Settings Management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  settingsService,
  TenantSettings,
  UpdateTenantSettingsDto,
  UserPreferences,
  UpdateUserPreferencesDto,
  SystemInfo,
} from '../services/settings.service';
import { CurrencyCode } from '../utils/currency';

interface UseSettingsState {
  tenantSettings: TenantSettings | null;
  userPreferences: UserPreferences | null;
  systemInfo: SystemInfo | null;
  loading: boolean;
  error: string | null;
}

export function useSettings() {
  const [state, setState] = useState<UseSettingsState>({
    tenantSettings: null,
    userPreferences: null,
    systemInfo: null,
    loading: true,
    error: null,
  });

  const fetchTenantSettings = useCallback(async () => {
    try {
      const response = await settingsService.getTenantSettings();
      if (response.success && response.data) {
        setState((prev) => ({ ...prev, tenantSettings: response.data! }));
      }
    } catch (error: any) {
      console.error('Failed to fetch tenant settings:', error);
    }
  }, []);

  const fetchUserPreferences = useCallback(async () => {
    try {
      const response = await settingsService.getUserPreferences();
      if (response.success && response.data) {
        setState((prev) => ({ ...prev, userPreferences: response.data! }));
      }
    } catch (error: any) {
      console.error('Failed to fetch user preferences:', error);
    }
  }, []);

  const fetchSystemInfo = useCallback(async () => {
    try {
      const response = await settingsService.getSystemInfo();
      if (response.success && response.data) {
        setState((prev) => ({ ...prev, systemInfo: response.data! }));
      }
    } catch (error: any) {
      console.error('Failed to fetch system info:', error);
    }
  }, []);

  const fetchAllSettings = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await Promise.all([
        fetchTenantSettings(),
        fetchUserPreferences(),
        fetchSystemInfo(),
      ]);
      setState((prev) => ({ ...prev, loading: false }));
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load settings',
      }));
    }
  }, [fetchTenantSettings, fetchUserPreferences, fetchSystemInfo]);

  const updateTenantSettings = useCallback(async (data: UpdateTenantSettingsDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await settingsService.updateTenantSettings(data);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          tenantSettings: response.data!,
          loading: false,
        }));
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update tenant settings',
      }));
      throw error;
    }
  }, []);

  const updateUserPreferences = useCallback(async (data: UpdateUserPreferencesDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await settingsService.updateUserPreferences(data);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          userPreferences: response.data!,
          loading: false,
        }));
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update user preferences',
      }));
      throw error;
    }
  }, []);

  const uploadLogo = useCallback(async (file: File) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await settingsService.uploadLogo(file);
      if (response.success && response.data) {
        // Update tenant settings with new logo URL
        await updateTenantSettings({ logo: response.data.url });
        return response.data.url;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to upload logo',
      }));
      throw error;
    }
  }, [updateTenantSettings]);

  const uploadFavicon = useCallback(async (file: File) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await settingsService.uploadFavicon(file);
      if (response.success && response.data) {
        // Update tenant settings with new favicon URL
        await updateTenantSettings({ favicon: response.data.url });
        return response.data.url;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to upload favicon',
      }));
      throw error;
    }
  }, [updateTenantSettings]);

  const testEmail = useCallback(async (email: string) => {
    try {
      const response = await settingsService.testEmail(email);
      return response.data;
    } catch (error: any) {
      console.error('Failed to test email:', error);
      throw error;
    }
  }, []);

  const backupDatabase = useCallback(async () => {
    try {
      const response = await settingsService.backupDatabase();
      return response.data;
    } catch (error: any) {
      console.error('Failed to backup database:', error);
      throw error;
    }
  }, []);

  const getTaxRate = useCallback((): number => {
    // Return taxRate from tenant settings or default to 16%
    return state.tenantSettings?.taxRate ?? 16;
  }, [state.tenantSettings]);

  const getVATInclusive = useCallback((): boolean => {
    // Return vatInclusive from tenant settings or default to false
    return state.tenantSettings?.vatInclusive ?? false;
  }, [state.tenantSettings]);

  const getCurrency = useCallback((): CurrencyCode => {
    // Return currency from tenant settings or default to KES (Kenya Shillings)
    return (state.tenantSettings?.currency as CurrencyCode) ?? 'KES';
  }, [state.tenantSettings]);

  const getDefaultPrintFormat = useCallback((): 'standard' | 'thermal' => {
    // Return defaultPrintFormat from tenant settings or default to 'standard'
    return (state.tenantSettings?.defaultPrintFormat as 'standard' | 'thermal') ?? 'standard';
  }, [state.tenantSettings]);

  const refresh = useCallback(() => {
    fetchAllSettings();
  }, [fetchAllSettings]);

  useEffect(() => {
    fetchAllSettings();
  }, [fetchAllSettings]);

  return {
    tenantSettings: state.tenantSettings,
    userPreferences: state.userPreferences,
    systemInfo: state.systemInfo,
    loading: state.loading,
    error: state.error,
    updateTenantSettings,
    updateUserPreferences,
    uploadLogo,
    uploadFavicon,
    testEmail,
    backupDatabase,
    getTaxRate,
    getVATInclusive,
    getCurrency,
    getDefaultPrintFormat,
    refresh,
  };
}